/** A status path: chevron steps in order, each done, current, blocked, ahead or not checked.
 *
 * The record page's "where this stands" (Uploaded, Read, Reviewed, Verified, In ledger) and the
 * filing's path (`PathToFiling`) are the same drawing. **State is a shape and a word, never colour
 * alone** (design rule 4): every state has its own glyph and every step carries its state in text
 * (`STATE_WORD`), visible as a title on the current step and read by a screen reader on all. The
 * steps are statements the server made (`screens/filingSpine.ts` says which for a filing), so this
 * component only draws: it decides nothing and counts nothing.
 *
 * The current step is drawn larger when `emphasis="current"` (the filing path); the record page's
 * path keeps them one size.
 */

import {
  CheckCircle,
  Circle,
  CircleNotch,
  MinusCircle,
  Question,
  XCircle,
} from "@phosphor-icons/react";
import type { Icon } from "@phosphor-icons/react";
import { useEffect, useRef } from "react";

import { STATE_WORD } from "../../screens/filingSpine";
import type { SpineStep, StepState } from "../../screens/filingSpine";
import styles from "./StatusPath.module.css";

const GLYPH: Record<StepState, { icon: Icon; weight: "fill" | "bold" | "regular" }> = {
  done: { icon: CheckCircle, weight: "fill" },
  current: { icon: CircleNotch, weight: "bold" },
  blocked: { icon: XCircle, weight: "fill" },
  ahead: { icon: Circle, weight: "regular" },
  unknown: { icon: Question, weight: "bold" },
  na: { icon: MinusCircle, weight: "regular" },
};

export function StatusPath({
  steps,
  label,
  emphasis = "none",
  testId,
}: {
  steps: readonly SpineStep[];
  /** What the list is, for a screen reader: "Where this record stands". */
  label: string;
  emphasis?: "none" | "current";
  testId?: string;
}) {
  const list = useRef<HTMLOListElement>(null);
  // **A path that scrolls can be reached by keyboard** (WCAG 2.1.1, axe `scrollable-region-focusable`):
  // at 320px the five stations are wider than the card and `.path` scrolls sideways, and its steps are
  // not controls, so Tab never got there. Whether it scrolls is a fact of layout, so it is measured here
  // and the list takes a tab stop only while it does. It stays a list (the shared hook would make it a
  // region, and a `li` needs its list).
  useEffect(() => {
    const el = list.current;
    if (!el) return undefined;
    const apply = () => {
      if (el.scrollWidth > el.clientWidth + 1) el.tabIndex = 0;
      else el.removeAttribute("tabindex");
    };
    apply();
    if (typeof ResizeObserver === "undefined") return undefined;
    const resize = new ResizeObserver(apply);
    resize.observe(el);
    return () => resize.disconnect();
  }, [steps]);
  return (
    <ol
      ref={list}
      className={`${styles.path} ${emphasis === "current" ? styles.emphasised : ""}`}
      aria-label={label}
      data-testid={testId}
    >
      {steps.map((step) => {
        const { icon: Glyph, weight } = GLYPH[step.state];
        return (
          <li
            key={step.key}
            className={styles.step}
            data-state={step.state}
            data-step={step.key}
            data-here={step.here ? "" : undefined}
          >
            <span className={styles.chevron}>
              <Glyph size={16} weight={weight} aria-hidden="true" className={styles.mark} />
              <span className={styles.label} title={step.label}>
                {step.label}
              </span>
              <span className="visuallyHidden">: {STATE_WORD[step.state]}</span>
              {step.note ? <span className="visuallyHidden">, {step.note}</span> : null}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
